import { ReactNode } from 'react'
import { Box } from '@mui/material'
import { discrepancyPillSx } from './shared'

type DiscrepancyPillProps = {
  hasDiscrepancy: boolean
  emphasized?: boolean
  children: ReactNode
}

/**
 * A tinted pill hugging a discrepancy cell's value, kept `display: inline` so it
 * paints its tint/padding/ring without growing the line box and changing row
 * height (rows are aligned by eye against the opposite table); `emphasized` is
 * the stronger hover/open treatment applied across the discrepancy's match group.
 */
export default function DiscrepancyPill({
  hasDiscrepancy,
  emphasized,
  children,
}: DiscrepancyPillProps) {
  if (!hasDiscrepancy) return <>{children}</>

  return (
    <Box
      component="span"
      data-emphasized={emphasized ? 'true' : undefined}
      sx={{
        // Plain inline (NOT inline-flex/inline-block): vertical padding + tint +
        // ring paint but never grow the line box, so the row height can't change.
        display: 'inline',
        whiteSpace: 'nowrap',
        ...discrepancyPillSx('warning', !!emphasized),
        // discrepancyPillSx's vertical margin (a cancel-the-padding trick for
        // FieldItem's block layout) is unnecessary and irrelevant here.
        my: 0,
      }}
    >
      <Box
        component="span"
        data-testid="cell-discrepancy-dot"
        sx={{
          display: 'inline-block',
          verticalAlign: 'middle',
          width: 6,
          height: 6,
          borderRadius: '50%',
          bgcolor: 'warning.main',
          mr: 0.75,
        }}
      />
      {children}
    </Box>
  )
}
